import { Theme } from "@emotion/react";
import { FormControl, RadioGroup, FormControlLabel, Radio, useTheme } from "@mui/material";
import { fontSizes, inlineTextIconMargin } from "@iventis/styles/src/atomic-rules";
import React, { FunctionComponent } from "react";
import { styled } from "@iventis/styles";

interface EnumToRadioButtonsProps<EnumType> {
    radioButtons: ButtonDetails<EnumType>[];
    value: EnumType;
    onChange: (value: EnumType) => void;
    columns?: number;
}

export interface ButtonDetails<Type> {
    name: string;
    description?: string;
    type: Type;
    disabled?: boolean;
    icon?: React.ReactNode;
    testId?: string;
}

/**
 * A component which displays different enum values as radio buttons
 * @param radioButtons - An array of radio buttons that are going to be show,
 * contains enum value (enum), name (string) and description (string)
 * @param value - Enum which is being represented as radio buttons
 * @param onChange - Function which handles a value change in the radio group
 */
// eslint-disable-next-line comma-spacing
const EnumToRadioButtons = <EnumType,>({ radioButtons, value, onChange, columns = 1 }: EnumToRadioButtonsProps<EnumType>) => {
    const theme = useTheme<Theme>();
    return (
        <FormControl fullWidth component="fieldset">
            <StyledRadioGroup value={value} columns={columns}>
                <RadioButtonsGrid columns={columns}>
                    {radioButtons.map((button) => (
                        <RadioButtonContainer key={button.type.toString()}>
                            <FormControlLabel
                                value={button.type}
                                control={
                                    <Radio
                                        disabled={button.disabled}
                                        size="small"
                                        sx={{
                                            color: theme.typographyColors.core,
                                            backgroundColor: theme.typographyColors.blank,
                                            width: 15,
                                            height: 15,
                                            padding: "0px",
                                            margin: "0px 10px",
                                            marginTop: "4px",
                                            alignSelf: "flex-start",
                                            "&.Mui-checked": {
                                                color: theme.typographyColors.core,
                                            },
                                            ":hover": {
                                                backgroundColor: theme.typographyColors.blank,
                                            },
                                        }}
                                        data-cy={button.testId}
                                    />
                                }
                                label={<RadioButtonDescription header={button.name} description={button.description} disabled={button.disabled} icon={button.icon} />}
                                onChange={() => onChange(button.type)}
                            />
                        </RadioButtonContainer>
                    ))}
                </RadioButtonsGrid>
            </StyledRadioGroup>
        </FormControl>
    );
};

const RadioButtonDescription: FunctionComponent<{ header: string; description?: string; disabled?: boolean; icon?: React.ReactNode }> = ({
    header,
    description,
    disabled,
    icon,
}) => (
    <>
        <RadioButtonTitleContainer>
            <HeaderText disabled={disabled}>{header}</HeaderText>
            {icon != null && icon}
        </RadioButtonTitleContainer>
        {description && <DescriptiveText disabled={disabled}>{description}</DescriptiveText>}
    </>
);

const RadioButtonTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${inlineTextIconMargin};
    align-items: center;
`;

const RadioButtonContainer = styled.div`
    margin: 10px;
`;

const StyledRadioGroup = styled(RadioGroup)<{ columns?: number }>`
    ${({ columns }) => columns && columns > 1 && "width: fit-content;"}
`;

const RadioButtonsGrid = styled.div<{ columns?: number }>`
    display: grid;
    gap: 0 16px;
    margin: 10px 0;
    ${({ columns }) =>
        columns && columns > 1
            ? `
        grid-auto-flow: column;
        grid-template-rows: repeat(${columns}, 1fr);
    `
            : `
        grid-template-columns: 1fr;
    `}
`;

export const DescriptiveText = styled.div<{ disabled?: boolean }>`
    color: ${({ theme, disabled }: { theme: Theme; disabled: boolean }) => (disabled ? theme.shades.three : theme.shades.two)};
    font-size: ${fontSizes.xSmall};
`;

export const HeaderText = styled.div<{ disabled?: boolean }>`
    font-size: ${fontSizes.small};
    font-weight: 410;
    color: ${({ theme, disabled }: { theme: Theme; disabled: boolean }) => (disabled ? theme.shades.three : theme.primaryColors.subduedMono)};
`;

export default EnumToRadioButtons;
